import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { companyInfo } from '../mock';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyLocation: '',
    projectTimeline: '',
    budgetRange: '',
    preferredStyle: '',
    message: '',
    consultationDate: '',
    consultationTime: '10:00'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.phone || !formData.message) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      if (!formData.consultationDate) {
        toast({
          title: "Date Required",
          description: "Please select your preferred consultation date.",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Submit to backend API
      const response = await axios.post(`${API}/consultation`, formData);
      
      if (response.data.success) {
        toast({
          title: "Consultation Scheduled!",
          description: `Your consultation has been scheduled for ${response.data.start_time}. A Google Meet link has been sent to ${formData.email}.`,
        });
        
        // Show meet link
        if (response.data.meet_link) {
          setTimeout(() => {
            toast({
              title: "Google Meet Link",
              description: `Meeting Link: ${response.data.meet_link}`,
            });
          }, 2000);
        }
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyLocation: '',
          projectTimeline: '',
          budgetRange: '',
          preferredStyle: '',
          message: '',
          consultationDate: '',
          consultationTime: '10:00'
        });
      }
    } catch (error) {
      console.error('Error submitting consultation:', error);
      toast({
        title: "Booking Failed",
        description: error.response?.data?.detail || "Failed to schedule consultation. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-[#c9a961]/10 text-[#c9a961] rounded-full text-sm font-semibold">
              Get In Touch
            </span>
          </div>
          
          <h2
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Start Your Dream Home{' '}
            <span className="text-[#c9a961]">Journey</span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to bring your vision to life? Contact us today for a free consultation and let's discuss how we can create your perfect luxury home.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-[#c9a961]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-[#c9a961]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <p className="text-gray-600">{companyInfo.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-[#c9a961]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-[#c9a961]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600">{companyInfo.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-[#c9a961]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#c9a961]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Location</h4>
                    <p className="text-gray-600">{companyInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Free Consultation Banner */}
            <div className="bg-gradient-to-br from-[#c9a961] to-[#b89851] rounded-xl p-6 text-white">
              <Calendar className="h-8 w-8 mb-3" />
              <h3 className="text-xl font-bold mb-2">Free Consultation</h3>
              <p className="text-white/90 text-sm leading-relaxed">
                Schedule a complimentary Google Meet consultation to discuss your project requirements and vision.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-gradient-to-br from-[#faf8f5] to-[#f5f5f0] rounded-xl p-8 shadow-lg">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    placeholder="+61 4XX XXX XXX"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Property Location
                  </label>
                  <Input
                    type="text"
                    placeholder="Suburb, Sydney"
                    value={formData.propertyLocation}
                    onChange={(e) => handleChange('propertyLocation', e.target.value)}
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Timeline
                  </label>
                  <Select onValueChange={(value) => handleChange('projectTimeline', value)}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immediate (0-3 months)</SelectItem>
                      <SelectItem value="short">Short-term (3-6 months)</SelectItem>
                      <SelectItem value="medium">Medium-term (6-12 months)</SelectItem>
                      <SelectItem value="long">Long-term (12+ months)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Budget Range
                  </label>
                  <Select onValueChange={(value) => handleChange('budgetRange', value)}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-500k">Less than $500K</SelectItem>
                      <SelectItem value="500k-1m">$500K - $1M</SelectItem>
                      <SelectItem value="1-2m">$1M - $2M</SelectItem>
                      <SelectItem value="2-3m">$2M - $3M</SelectItem>
                      <SelectItem value="3-5m">$3M - $5M</SelectItem>
                      <SelectItem value="5m+">$5M+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Type
                  </label>
                  <Select onValueChange={(value) => handleChange('preferredStyle', value)}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new-home">New Home Build</SelectItem>
                      <SelectItem value="duplex">Duplex Construction</SelectItem>
                      <SelectItem value="renovation">Luxury Renovation</SelectItem>
                      <SelectItem value="extension">Home Extension</SelectItem>
                      <SelectItem value="pool-deck">Pool & Deck Installation</SelectItem>
                      <SelectItem value="contemporary">Contemporary Design</SelectItem>
                      <SelectItem value="french">French Provincial</SelectItem>
                      <SelectItem value="classic">Classic Design</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Consultation Date *
                  </label>
                  <Input
                    type="date"
                    value={formData.consultationDate}
                    onChange={(e) => handleChange('consultationDate', e.target.value)}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Time *
                  </label>
                  <Input
                    type="time"
                    value={formData.consultationTime}
                    onChange={(e) => handleChange('consultationTime', e.target.value)}
                    required
                    className="bg-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Business hours: 8 AM - 6 PM</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Details / Message *
                </label>
                <Textarea
                  placeholder="Tell us about your dream home project..."
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  required
                  rows={5}
                  className="bg-white"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-[#c9a961] hover:bg-[#b89851] text-white transition-colors"
              >
                {isSubmitting ? (
                  <span>Submitting...</span>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Submit Enquiry
                  </>
                )}
              </Button>

              <p className="text-sm text-gray-500 mt-4 text-center">
                We'll review your enquiry and schedule a Google Meet consultation at your preferred time.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;